import { BarcodeFormat } from "@zxing/library";
import Field from "../../../components/scenes/Layers/fields/Field";
import { MimeType } from "../../../constants/misc";
import { CHECK_CARD_TYPE, Check, FruitProps, LAYER_STAGE, LAYER_TAB_CATEGORIES, LAYER_TYPE, Layer, VIEW } from "../../Layers/constants";
import { OnloadLayerCallbackProps } from "./onload_layer_callbacks/onload_layer_callbacks";


export type ConfigItemType = {
    id: string,
    type: string,
    value: string,
    config: object
}
export interface MinMaxAvg {
    min: string | number | null;
    max: string | number | null;
    avg: string | number | null;
    avg_raw: number | null;
}

export interface FruitConfig {
    value: string;
    text: string;
    default_variety: string;
    a_fruit?: string;
    n?: string;
    n_cap?: string;
    // lang?: object // todo remove this
    internal_colors?: CategoryType[];
    external_colors?: CategoryType[];
    ripening_fields?: Field[]; // Fields to be used on ripening graph
    unit_of_account?: string;
}


export interface CheckSummary {
    meta?: Field[];
    pressure_min_max?: {
        field: string;
        unity?: string;
        speed?: boolean; // show ripening speed compared to earlier check
        categories?: CategoryType[];
    } | false;
    categories?: {
        field: string;
        title?: string | boolean;
        categories?: CategoryType[];
    }[]; // array of categories to show in the summary, can be used to generate pie charts
}

export interface FormFieldOnChangeProps {
    setValue: (field: string, value: unknown) => void;
    field: string;
    value: any;
    object: any;
}
export interface IFormField<T = string> {
    label: string,
    name: string,
    type: T,
    mandatory?: boolean,
    onchange_callback?: string | (({ setValue, field, value, object }: FormFieldOnChangeProps) => void),
    validate?: (value) => boolean,
    get_value?: (value) => any,
    invalid_hint?: string,
    description?: string,
    disabled?: boolean
    default_value?: any;
    default_today?: boolean; // for date fields: pre-fill today's date (yyyy-mm-dd) at form init, still editable
    default_value_from_layer_key?: string; // copy the default from layer[<key>] at form init
    default_value_from_check_meta?: string; // copy the default from check[<key>] (flattened check_meta) at form init
    pattern?: string
    defect_position?: string[];
    defect_severity?: string[];
    deprecated?: boolean; // set deprecated to true to hide the field as predifined form
}

export interface GeneralFormOptionType {
    value: string | number | boolean;
    label?: string;
    text?: JSX.Element | string;
    hidden?: boolean;
    disabled?: boolean;
    className?: string;
    color?: string;
}
export type RadioOption = GeneralFormOptionType
export type RadioOptionWithLabel = GeneralFormOptionType
export type OptionFlagged = GeneralFormOptionType

export interface SelectOption {
    value: string;
    label: string;
    hidden?: boolean;
}

export interface ImageOption extends OptionFlagged {
    blob_name: string;
    value: string;
}

export interface FormDateRadioButtonField extends IFormField<"date-radiobutton"> {
    backward: number;
    forward: number;
}
export interface FormSelectManagedField extends IFormField<"select-managed"> {
    multi?: boolean;
    options?: SelectOption[];
    lg?: number;
    hide_when_no_options?: boolean;
    return_array?: boolean;
}
export interface FormRadioField extends IFormField<"radio"> {
    options: RadioOption[] | OptionFlagged[] | RadioOptionWithLabel[];
    lg?: number;
    // color?: ColorConfig[];
}
export interface FormImageRadioButtonsField extends IFormField<"image-radiobutton"> {
    options: ImageOption[];
}

export interface FormNumberField extends IFormField<"number"> {
    min?: number;
    max?: number;
    unity?: string;
    invalid?: boolean;
    lg?: number;
}

export interface FormTextField extends IFormField<"text"> {
    lg?: number;
}
export interface FormRadioManagedField extends IFormField<"radio-managed"> {
    options_name?: string;
}
export interface FormCheckboxManagedField extends IFormField<"checkbox-managed"> {
    options_name?: string;
}
export interface FormQRCodeScannerField extends IFormField<"text-qrcode-scanner"> {
    /**
     * Meta keys of the options (e.g. batch) shown next to the label and searchable in the dropdown
     */
    meta_options?: string[];
}
export interface FormFtaDeviceField extends IFormField<"fta-device-field"> {
    /**
     * key to grab value from fta device response object
     */
    data_key: string;
    data_value_type: "text" | "number" | "boolean";
}
export interface FormLayerChildrenSelect extends IFormField<"layer-select-children"> {
    meta_options?: string[];
}

export interface FormLayerSelect extends IFormField<"layer-select"> {
    /**
     * If set, the layer select will only show layers of this type
     */
    layer_type?: string;

    /**
     * If set to true, the layer select will allow multiple layers to be selected
     */
    multi?: boolean;
}

export interface FormSingleSelectField extends IFormField<"single-select"> {
    options: SelectOption[];
    lg?: number;
    multi?: boolean;
    return_array?: boolean;
}

export interface RequiredImage {
    type: string;
    label: string;
    mandatory?: boolean;
    display_filter?: {
        fruit_types?: string[];
        exclude_fruit_types?: string[];
        layer_types?: string[];
        meta_conditions?: any[];
    };
}

export interface FormFruitImagesField extends IFormField<"fruit-images"> {
    options?: RequiredImage[];
}

export interface FormInlineFormField extends IFormField<"inline-form"> {
    fields: FormField[];
    description?: string;
    lg?: number;
}

export type FormEmailField = IFormField<"email">;

export interface FormObjectListField extends IFormField<"object-list"> {
    fields: FormField[] | ((item: any) => FormField[]);
    getItemTitle: (item: any) => string;
    validate: (item: any) => boolean;
    disabled: boolean;
}

export enum FormFileSelectFieldReadFormat {
    Base64 = "base64",
    Raw = "raw"
}

export interface FormFileSelectField extends IFormField<"file-select"> {
    accept: MimeType[]
    readFormat: FormFileSelectFieldReadFormat;
}

export interface FormTextListField extends IFormField<"text-list"> {
    field: FormTextField | FormEmailField;
}

export interface FormAlertField extends IFormField<"alert"> {
    color?: "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark";
}

export type FormField =
    | FormDateRadioButtonField
    | FormSelectManagedField
    | FormTextField
    | FormNumberField
    | FormRadioManagedField
    | FormQRCodeScannerField
    | FormFtaDeviceField
    | FormSingleSelectField
    | FormRadioField
    | FormImageRadioButtonsField
    | FormCheckboxManagedField
    | FormLayerSelect
    | FormLayerChildrenSelect
    | FormFruitImagesField
    | FormInlineFormField
    | FormEmailField
    | FormObjectListField
    | FormFileSelectField
    | FormTextListField
    | FormAlertField;

export type BarcodeFilter = {
    filter: (result: string) => string | null;
    formatsToSupport: BarcodeFormat[];
} | ((result: string) => string | null);

export interface BaseCheckLocation {
    text?: string;
    value?: string;
    flow?: { path: string; disable_required_images: boolean }[];
    inline_form?: string[]; // same as flow but without the first step (device results)
    fruit_form?: FormField[]; // primary form for fruit
    secondary_fruit_form?: FormField[]; // secondary form for fruit
    fruit_types?: string[]; // TODO: explicitly set type in fruit_types
    summary_fruit_fields?: string[]; // TODO: refactor to new field definition
    show_atron_color?: boolean;
    show_atron_results_bottom_summary?: boolean;
    meta_form_summary?: object[];
    meta_form_layer?: FormField[];
    pre_check_form?: FormField[];
    visual_check_form?: FormField[];
    visual_check_defect_form?: FormField[];
    secondary_visual_check_form?: FormField[];
    secondary_visual_check_defect_form?: FormField[];
    defect_groups?: any[];
    device_result_action?: string | boolean;
    only_show_latest_check_on_child?: boolean; // only show latest check on child layer in calender overview
    card_caption_field?: string; // check field or check meta key shown under the check card in the calender overview
    show_atron_weight?: boolean;
    required_images?: RequiredImages[];
    calibration_stage?: LAYER_STAGE; // From which calibration stage should the calibration be used either RIPENING or PRODUCTION
    require_another_check?: string; // require another check before running this check
    show_external_defects?: boolean;
    advance_ripening_classes_form?: string[] | FormField[];
    advance_ripening_show_external_defects?: boolean;
    show_scanner_button_on_check_page?: boolean;
    show_in_barcode_scanner_shortcut?: boolean;
    show_advanced_fta_check_page?: boolean;
    cta_form_title?: string;
    cta_form?: FormField[];
    cta_form_location?: string;
    unit_of_account?: string;
    bulk_input_modal?: string;
    enable_duplicate_fruit?: boolean;
    requires_active_ripening_cycle?: boolean;
    show_fruit_per_box?: boolean;
    summary_layer_fields_pdf?: any[];
    summary_layer_fields_1?: any[];
    summary_layer_fields_2?: any[];
    summary_layer_fields_3?: any[];
    summary_layer_fields_4?: any[];
    summary_fruit_fields_v2?: any[];
    summary_min_max_breakdown?: any[];
    summary_category_charts?: any[];
    required_fruit_images?: RequiredImages[];
    retrospective_group_by_attributes?: string[];
    retrospective_group_by_fields?: { field: { label?: string; value: string }; display_filter?: object }[];
    retrospective_group_parent_types?: string[];
}

export interface CalibrationSettings {
    calibration_stage: LAYER_STAGE;
    min_fruit?: number;
    required_sample_size: number;
    device_result_action: string;
}
export interface CheckLocation extends BaseCheckLocation {
    is_linked_to_latest_location: any;
    text: string;
    /**
     * Optional list of overview tab values (stage.value) where this check location
     * should show "Start check" actions on the layer overview screen.
     * If omitted everywhere, legacy tab.start_check_types may still be used.
     */
    start_on_overview_tabs?: string[];
    is_hidden?: boolean;
    is_invalid?: boolean; // this location does not match with check.location (a fallback is used)
    is_manual?: boolean; // is this a manual check location (without device)
    allocate_to_stage?: LAYER_STAGE;
    /**
     * If set (and allocate_to_stage is set), allocate matching parents to the same stage as well.
     * This only allocates the parent layer(s) themselves (does not allocate the parent's other children).
     */
    allocate_parent_of_type?: string[];
    value: string;
    summary_fruit_fields: string[]; // TODO: refactor to new field definition
    meta_form_advance_ripening?: FormField[];
    advance_ripening_start_form?: FormField[];
    pressure_calibration_settings?: CalibrationSettings;
    position?: number;
    enable_send_notification?: boolean;
    check_email_recipients?: string;
    /**
     * If set, this function will be called to check if the user is allowed to create a check on this location
     * within the current state of the layer
     */
    // check_guard?: (layer:Layer) => {
    //     pass: boolean;
    //     message: string;
    // };
}

export interface FormOption {
    value: string;
    label: string;
}

interface RequiredImages {
    type: string;
    label: string | any;

}

interface PDFIntakeReport {
    value?: string;
    text?: string;
    template?: string;
    check_types?: string[];
    fallback_check_types?: string[];
    fallback_check_max_age_days?: { fruit_type: string; max_age_days: number }[];
    show_dry_matter?: boolean;
    business_rule_flag_field?: string;
    manual_flag_field?: string;
    show_send_email_button?: boolean;
    teams_that_can_send?: string[];
    view_type?: string[];
    defect_groups?: any[];
    title?: string;
    audience?: string[];
    external_defects_title?: string;
    internal_defects_title?: string;
    children_table_fields: Field[];
    children_check_summary_meta_fields?: Field[];
    children_check_external_defects?: Field[];
    children_check_internal_defects?: Field[];
    children_check_major_external_defects?: Field[];
    children_check_minor_external_defects?: Field[];
    children_check_unacceptable_defects?: Field[];
    check_meta_fields?: Field[];
    pallet_meta_fields?: Field[]; // only for ART
}
export interface LayerTypeConfig {
    allow_remove_allocation?: boolean;
    onload_callback?: string | ((props: OnloadLayerCallbackProps) => void);
    text: string;
    value: LAYER_TYPE;
    view_strategy: string; // TODO: clean up, Not in use
    parent_layer_type?: boolean | LAYER_TYPE;
    children_layer_type?: boolean | LAYER_TYPE;
    disabled?: boolean; // disable the radio button for creation of this layer type
    meta_form?: FormField[];
    meta_display: Field[];
    // checkLocations: CheckLocation[];
    label_format?: string; // add restriction to layer creation
    invalid_hint?: string;
    position?: number;

    show_edit_button?: boolean; // show edit button on left side of layer index
    show_link_layer_button?: boolean | string; // show link layer button on left side of layer index, pass string to define next action
    start_action?: string; // action to start something on layer index #TODO: create enum here
    show_images?: RequiredImages[]; // show images on layer index
    pdf_intake_report?: PDFIntakeReport; // config for pdf intake report
    get_pdf_report?: (layer: Layer, report_type: string) => PDFIntakeReport | undefined; // get pdf report config based on layer
    special_actions?: Array<{
        position?: number;
        type: string;
        [key: string]: any;
    }>; // special check button for top right of layer index
    show_allocate_button?: boolean; // show allocate button on layer index
    show_send_status_to_tenant_button?: boolean; // show "send pallet status to tenant" button on layer index (order layers)
    pdf_advance_ripening_report?: PDFIntakeReport; // hide allocate button on layer index
    is_creatable?: boolean; // is layer type selectable on creation
    hide_sub_pallets?: boolean; // hide sub pallets
    show_upload_reports_button?: boolean; // show upload reports button

    show_checks?: {
        view: VIEW;
        filter_field?: string;
        filter_location?: LAYER_STAGE | string;
        card_type?: CHECK_CARD_TYPE;
    };
    show_children?: {
        view?: VIEW;
        fields: Field[];
        title?: string;
        filter_field?: string;
        filter_location?: LAYER_STAGE | string;
    };
    show_box_weight_check?: {
        title?: string;
    };
    show_arrival_check?: {
        fields?: any[];
        title?: string;
    };
    show_advance_ripening?: {
        fields?: any[];
    };
    show_ripening_progress?: {
        view: VIEW;
        field?: Field | boolean;
        title?: string;
        filter_field?: string;
        filter_location?: LAYER_STAGE | string;
    } | false;
    show_ripening_progress_firmness_history_table?: {
        view: VIEW;
        title?: string;
        filter_field?: string;
        filter_location?: LAYER_STAGE | string;
    } | false;
    show_metric_cards?: false | null | { key: string; label: string }[];
    show_ripening_progress_pressure_history_table?: boolean;
    show_general_notes?: boolean;
    show_lab_check?: {
        title?: string;
    };
    arrival_check_meta_display?: Field[];
    enable_matching_attribute?: boolean;
    matching_attribute_name?: string;
    enable_children_limits?: boolean;
    max_children?: number;
    max_children_attribute_name?: string;

}

interface Page {
    [key: string]: string;
}

export interface ViewStrategy {
    title: string;
    description: string;
    view_strategy: VIEW;
    layer_type: LAYER_TYPE | null;
    overview_fields?: Field[]; // If you want to enable specific field per view strategy
}
export interface Stage {
    text: string;
    value: LAYER_STAGE;
    layer_type?: LAYER_TYPE | null;
    layer_overview_fields?: Field[]; // Replace with the actual type if not an array of strings
    view_strategy: VIEW;
    view_strategies?: ViewStrategy[] | false;
    hide_color_code?: boolean;
    status_color?: string;
    status_letter?: string;
    overview_filter_form?: Field[];
    limit?: number;
    default_create_layer_type?: LAYER_TYPE;
    filter_layer_meta?: Array<{ field: string; method: string; value: any; source?: "layer_meta" | "parent_meta" }>;
}

export interface Tab extends Stage {
    title: string;
    subtitle?: string | null;
    location: LAYER_STAGE;
    default_ordering: string;
    cta_form_title?: string;
    cta_form?: FormField[];
    display_for_teams?: string[];
    hide_for_teams?: string[];
    filter_teams?: string[];
    filter_facilities?: string[];
    facility_filter_meta_fields?: string | string[];
    max_days_in_facility?: number;
    max_days_in_stage?: number;
    planned_work_look_ahead_days?: number;
    tab_categories?: LAYER_TAB_CATEGORIES[];
    custom_component?: string;
    show_draft_reports?: string[];
    show_sent_reports?: string[];
    show_layers_without_planned_work?: string;
    fields: Field[];
    show_class_distribution?: boolean;
    start_check_types?: string[];

}


export interface CategoryType {
    gte?: boolean | number;
    lt?: number | boolean;
    gt?: boolean | number;
    lte?: number | boolean;
    label: string;
    value?: string | number;
    color: string;
    /**
     * Optional explicit SAP/external CODE1 for this bucket, consumed by the
     * external-status (Bakker SAP) integration for INSPCHAR=0010. Takes
     * precedence over the per-fruit default vocabularies when set.
     */
    external_code?: string;
}

// export interface RootConfigType {
//     fruit_types: ConfigItemType[]
//     check_locations: ConfigItemType[]
//     layer_types: ConfigItemType[]
//     pdf_reports: ConfigItemType[]
//     stages: ConfigItemType[]

// }

export interface FacilityLocation {
    location_id: string;
    label?: string;
    stage?: string;
    facility?: string;
}

export interface RootConfigType {
    currentPage?: string | undefined;
    overview_filter_form?: object[];
    logo_path?: string; // Blob path of the client logo uploaded to tenant config
    // EFQ-3850 — `date_format` / `datetime_format` live on `root_config`
    // (single source of truth resolved by ConfigProvider).
    root_config: { date_format?: string; datetime_format?: string } & Record<string, any>;
    // False while getAllActiveTenantConfigs is still fetching after a hard
    // page load (root_config is {} then). Feature gates that navigate away
    // when a flag is off must check this first or they bounce valid deep-links.
    configs_loaded?: boolean;
    fruit_types: any;
    check_locations: any;
    layer_types: any;
    pdf_reports: any;
    stages: any;
    get_fruit_types(): FruitConfig[];
    get_fruit_type(fruit_type: string): FruitConfig;
    get_fruit_form(layer: Layer, check: Pick<Check, "location" | "is_manual">, avocado: FruitProps): FormField[];
    get_check_required_images(layer: Layer, check: Pick<Check, "location" | "is_manual">): RequiredImage[];
    get_fruit_required_images(layer: Layer, check: Pick<Check, "location" | "is_manual">, fruit: FruitProps): RequiredImage[];
    get_fruit_type_external_colors(pallet: any): CategoryType[] | false;
    get_fruit_type_internal_colors(pallet: any): CategoryType[] | false;
    get_ripening_classes(pallet: any, fieldname: string): CategoryType[] | false;
    default_fruit_type: string;
    required_check_images?: RequiredImage[];
    plu?: boolean;// Use to add tenant form options on the the tenant config. should be refactored to a more generic solution
    has_purchase_order_number: boolean;
    default_manual: boolean;
    can_do_manual_check: boolean;
    summary_collapsed: boolean;
    ripening_target_options: boolean;
    indicate_soft_avocado_at_internal: boolean;
    indicate_quality_at_defects: boolean;
    suggest_color_stage: boolean;
    default_location: string;
    underweight?: object[];
    research_mode: boolean;
    bakker: boolean;
    invalidate_20x_firmness: boolean;
    show_device_toggle: boolean;
    // get_overview_fields(filter: LayerFilter): Field[]; // get columns for the overview screens based on current filter (stage and layer_type)
    pages: Page;
    lang: { [key: string]: string };
    first_check_page(layer: Layer, check: Pick<Check, "location" | "is_manual">, currentPage: string): Page | false;
    next_check_page(layer: Layer, check: Pick<Check, "location" | "is_manual">, currentPage: string): Page | false;
    prev_check_page(layer: Layer, check: Pick<Check, "location" | "is_manual">, currentPage: string): Page | false;
    // TODO: rename to base_location_config
    default_location_config: BaseCheckLocation;
    get_check_type_config(layer: Layer, check: Pick<Check, "location" | "is_manual">): CheckLocation | undefined;
    get_check_unit_of_account(layer: Layer, check: Pick<Check, "location" | "is_manual">): string;
    get_check_types_config(layer: Layer): CheckLocation[];
    filter_location_fields(rawLocation: any, filter: object): CheckLocation; // TODO: define type for rawLocation and displayFilter
    get_stage(stage: null | string | undefined): Stage;
    get_layer_config(layer: Layer): LayerTypeConfig;
    get_raw_layer_config(layer_type: string | undefined): any; // TODO make type for raw layer config, with unfiltered fields
    get_raw_check_location(check_location: string): any;
    get_layer_pdf_reports(layer: Layer): any
    get_pdf_report(layer: Layer, report_type: string): PDFIntakeReport | undefined;
    translate_atron_color_to_manual_color_stage(atron_color: number): string | number;
    dry_matter_classes?: CategoryType[];
    firmness_to_display_value: (firmness: number) => number | string;
    headerSecondaryLinks: string[];
    headerMainLinks: string[];
    barcode_filter: BarcodeFilter;
    use_barcode_scanner?: boolean;
    is_admin?: boolean;
    optionsYesNo?: FormOption[]; // TODO: can be removed ?
    is_location_calibrated(location: CheckLocation, layer: Layer): boolean
    get_defect_positions(): { options: Array<{ value: string; label: string }>; values: string[] };
    get_defect_severities(): { options: Array<{ value: string; label: string }>; values: string[] };
    get_onchange_callback(string): any;
    onchange_callbacks: { [key: string]: any };
    form_options: Record<string, FormOption[]>;
    facility_locations: Array<FacilityLocation>;
    get_facility_location(location_id: string): FacilityLocation | undefined;

    getOptions?: (tenant_id: string) => void,
    getConfigs?: (tenant_id: string) => void
    mock_signals?: {
        color: string,
        title: string,
        description: string,
        url: string,
        flag: string,
    }[];
    applyDisplayFilter: (fields: any, filter: any) => any;
    applyDisplayFilterFields: (fields: any, filter: any) => any;
    applyDisplayFilterLayerFields: (fields: any, filter: any) => any;
    setupFieldClassFromConfig: (fieldConfig: any, filter: any) => any;
    [key: string]: any;
}

export enum UserRole {
    TENANT_ADMIN = "TenantAdmin"
}

export enum FieldsToFetchFromItsOwnTable {
    SUPPLIER = "supplier",
    END_CLIENT = "end_client",
    GROWER = "grower",
    LOCATION_ID = "location_id",
    REPORT_TYPE = "report_type"
}
